import { LSCField } from '../../../models/LSCFields';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom, tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { ApiService } from '../../../core/api-service';
import { EMPTY, finalize, Observable } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Location } from '../../../models/locations-models/location';
import { HttpErrorResponse } from '@angular/common/http';
import { FeedbackService } from '../../../core/feedback.service';
import * as locationListSelectors from '../location-list/state/location-list-selectors';
import { selectAccountID } from '../../../store/global/core-selectors';
import { getFullNameForFieldID } from '../location-utils';

export enum UpdateType {
  IMPORT_ONLY = 'IMPORT_ONLY', UPDATE = 'UPDATE', MANUAL = 'MANUAL', CSV = 'CSV'
}

// The following fields do come up only in BLS updates.
export enum BLS_UPDATES_FIELD_NAMES {
  MANAGED = 'managed',
  PROFILE_PHOTO = 'profilePhoto',
  ADDED_TO_LSC = '_addedToLSC'
}

export interface BlsUpdate {
  id: number;
  accountId: number;
  fieldName: LSCField | BLS_UPDATES_FIELD_NAMES;
  previousValue: string;
  currentValue: string;
  type: UpdateType;
  timestamp: number;
}

export interface BlsUpdateState {
  loading: boolean;
  blsUpdates: BlsUpdate[];
}

@Injectable()
export class BlsUpdatesLocalStoreService extends ComponentStore<BlsUpdateState> {
  constructor(
      private store: Store,
      private apiService: ApiService,
      private feedbackService: FeedbackService) {
    super({
      loading: false,
      blsUpdates: [],
    });
    this.getBlsUpdates(this.store.select(locationListSelectors.selectFocusedLocation));
  }

  readonly sortedFieldNameFilterOptions$ =
      this.select(state => state.blsUpdates)
          .pipe(map((blsUpdates) => blsUpdates
                  .map((blsUpdate) => blsUpdate.fieldName)
                  // filter unique values
                  .filter((fieldName, i, fieldNames) =>
                      fieldNames.indexOf(fieldName) === i)
                  .sort((a, b) => {
                    const prettyA = this.getFieldNameByFieldID(a);
                    const prettyB = this.getFieldNameByFieldID(b);
                    return prettyA.localeCompare(prettyB);
                  })
                  .map(fieldId => ({
                    name: this.getFieldNameByFieldID(fieldId),
                    value: fieldId,
                  }))
              ),
          );

  readonly blsUpdatesState$: Observable<BlsUpdateState> = this.state$;


  // Updaters

  readonly updateBlsUpdates = this.updater((state, blsUpdates: BlsUpdate[]): BlsUpdateState => (
      { ...state, blsUpdates }
  ));

  readonly updateLoading = this.updater((state, loading: boolean): BlsUpdateState => (
      { ...state, loading }
  ));

  // Effects

  readonly getBlsUpdates = this.effect((trigger$: Observable<Location>) => {
    return trigger$.pipe(
        tap(() => {
          this.updateLoading(true);
        }),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([location, accountID]) => location ?
            this.apiService.getLocationUpdates(
                accountID, location.id).pipe(
                tapResponse({
                  next: (res) => {
                    this.updateBlsUpdates(res.result);
                  },
                  error: (error: HttpErrorResponse) => {
                    this.feedbackService.showAlertSnackbar({
                      data: {
                        userFriendlyMessage: 'Loading BLS updates failed.',
                        detailedInfoFromBE: error?.error?.message ? [error.error.message] : null,
                      },
                    }, error?.status);
                  },
                }),
                finalize(() => this.updateLoading(false)),
            ) : EMPTY.pipe(
                finalize(() => {
                  this.updateBlsUpdates([]);
                  this.updateLoading(false);
                }),
            ),
        ),
        takeUntil(this.destroy$),
    );
  });



  // HELPERS


  /**
   * Returns a "pretty" name for a field ID
   * @param fieldId
   */
  public getFieldNameByFieldID(fieldId: string): string {
    const blsUpdatesFieldNameDictionary = {
      [BLS_UPDATES_FIELD_NAMES.MANAGED]: 'Managed',
      [BLS_UPDATES_FIELD_NAMES.PROFILE_PHOTO]: 'Profile Photo',
      [BLS_UPDATES_FIELD_NAMES.ADDED_TO_LSC]: 'Added to LSC',
    };

    if (blsUpdatesFieldNameDictionary[fieldId]) {
      return blsUpdatesFieldNameDictionary[fieldId];
    }

    return getFullNameForFieldID(fieldId);
  }


}

